import { PrimitiveAtom, useAtom } from 'jotai'
import { useCallback, useEffect, useState } from 'react'
import { Accept, FileRejection, useDropzone } from 'react-dropzone'

import { BigFileDrawer, EmptyFileDrawer, InvalidFileDrawer, ResolutionDrawer } from '@/pages/components/documents/drawers'
import BigFileNameDrawer from '@/pages/components/documents/drawers/bigFileNameDrawer'
import TooBigBatchFilesDrawer from '@/pages/components/documents/drawers/tooBigBatchOfFiles'
import ProhibitedUpload from '@/pages/components/documents/prohibitedUpload'
import DropZone from '@/pages/components/dropzone/DropZone'
import { getBaseName, uploadInProgress } from '@/store/documents/DocumentsAtom'
import { chatIdAtom, chatSettingsAtom, checkHealth, getChatSettings, saveSettings } from '@/store/gigiChat/ChatAtom'
import { quotasAtom } from '@/store/gigiChat/Quotas'
import { translationFileExtensionsAtom, uploadTranslateFiles } from '@/store/translate/'
import { IMimeType } from '@/types/documents/File'
import { IConversationSettings } from '@/types/gigiChat/ConversationSettings'
import { IQuotas } from '@/types/gigiChat/Quotas'
import { ITranslatedDocs } from '@/types/translation/Translate'

import InvalidCharactersFileNameDrawer from '../drawers/invalidCharactersFileName'
import MoreThaOneHundredToUpload from '../drawers/moreThaOneHundredToUpload'
import TooManyFilesToUpload from '../drawers/tooManyFilesToUpload'

const MAX_TOTAL_SIZE = 30000000
const MAX_FILE_SIZE = 30000000
const MAX_NUMBER_OF_FILES = 20
const TOTAL_NUMBER_OF_FILES = 100

const Container = () => {
    const [chatId] = useAtom<PrimitiveAtom<string>>(chatIdAtom)
    const [chatSettings] = useAtom<PrimitiveAtom<IConversationSettings | null>>(chatSettingsAtom)
    const [fileExtensions] = useAtom<PrimitiveAtom<Array<IMimeType> | null>>(translationFileExtensionsAtom)
    const [quotas] = useAtom<PrimitiveAtom<IQuotas | null>>(quotasAtom)
    const [isUploading, setIsUploading] = useAtom(uploadInProgress)
    const [invalidFile, setInvalidFile] = useState<Array<File>>([])
    const [emptyFile, setEmptyFile] = useState<Array<File>>([])
    const [bigFile, setBigFile] = useState<Array<File>>([])
    const [bigFileName, setBigFileName] = useState<Array<File>>([])
    const [validFiles, setValidFiles] = useState<Array<File>>([])
    const [isTooBig, setIsTooBig] = useState<boolean>(false)
    const [duplicateFile, setDuplicateFile] = useState<Array<File>>([])
    const [isTooManyFilesError, setIsTooManyFilesError] = useState<boolean>(false)
    const [isOverTotalNumbersOfFiles, setIsOverTotalNumbersOfFiles] = useState<boolean>(false)
    const [specialCharsFiles, setSpecialCharactersFiles] = useState<Array<File>>([])

    const [isVpn, setIsVpn] = useState<boolean | null>(null)

    const containsSpecialCharacters = (fileName: string) => {
        // Define a regex pattern to match any special character
        const pattern = /[#%*:<>?/|'\\""]/
        const baseName = fileName.substring(0, fileName.lastIndexOf('.'))

        if (baseName.charAt(0) === '~') {
            return true
        }

        // Test the file name against the pattern
        return pattern.test(baseName)
    }

    const fileValidator = (file: File) => {
        const fileList = chatSettings?.processingStrategyTranslation.docs
        const found = fileList && fileList.find((item: ITranslatedDocs) => item.name === file.name)
        const hasSpecialCharacters = containsSpecialCharacters(file.name)

        if (hasSpecialCharacters) {
            return {
                code: 'special-characters',
                message: 'special-characters',
            }
        } else if (file.size === 0) {
            return {
                code: 'empty-file',
                message: 'empty-file',
            }
        } else if (file.size > MAX_FILE_SIZE) {
            return {
                code: 'too-big-file',
                message: 'too-big-file',
            }
        } else if (file.name.length > 255) {
            return {
                code: 'too-big-file-name',
                message: 'too-big-file-name',
            }
        } else if (found) {
            return {
                code: 'duplicate-name',
                message: 'duplicate-name',
            }
        }

        return null
    }

    const onUpload = useCallback(() => {
        //set uploading state
        setIsUploading(true)
        const formData = new FormData()
        const filesToUpload = [...validFiles]

        filesToUpload.forEach((file: File) => {
            formData.append('files', file)
        })

        if (filesToUpload.length === 0) {
            //if there are no files to upload than we should hide upload in progress component
            setIsUploading(false)
        }

        if (filesToUpload.length > 0) {
            formData.append('chat_id', chatId)
            void uploadTranslateFiles(formData)
                .then(async (response) => {
                    setIsUploading(false)
                    if (response && response.status !== 500) {
                        const settings = (await getChatSettings(chatId)) as IConversationSettings
                        saveSettings(settings)
                    }
                })
                .catch(() => setIsUploading(false))
            setValidFiles([])
        }
    }, [validFiles, setIsUploading, chatId])

    const onDrop = useCallback(
        (acceptedFiles: File[], fileRejections: FileRejection[]) => {
            const docsLength = chatSettings?.processingStrategyTranslation.docs.length || 0
            if (acceptedFiles.length + fileRejections.length + docsLength > TOTAL_NUMBER_OF_FILES) {
                setIsOverTotalNumbersOfFiles(true)
                return
            } else if (acceptedFiles.length + fileRejections.length > MAX_NUMBER_OF_FILES) {
                setIsTooManyFilesError(true)
                return
            }
            //set uploading state
            setIsUploading(true)
            const rejectedFile = []
            const invalidFiles: Array<File> = []
            const duplicateFiles: Array<File> = []
            const emptyFiles: Array<File> = []
            const tooBigFiles: Array<File> = []
            const tooBigFileName: Array<File> = []
            const specialCharsItems: Array<File> = []
            let tooBigBatch: boolean = false

            let totalSize = 0

            acceptedFiles.forEach((file) => {
                totalSize += file.size
            })

            if (totalSize < MAX_TOTAL_SIZE) {
                //save accepted files
                setValidFiles(acceptedFiles)
            } else {
                tooBigBatch = true
                setIsTooBig(true)
            }

            !tooBigBatch &&
                fileRejections.forEach(({ file, errors }) => {
                    rejectedFile.push(file.name)

                    errors.forEach((e) => {
                        if (e.code === 'file-invalid-type') {
                            invalidFiles.push(file)
                        } else if (e.code === 'empty-file') {
                            emptyFiles.push(file)
                        } else if (e.code === 'too-big-file') {
                            tooBigFiles.push(file)
                        } else if (e.code === 'duplicate-name') {
                            duplicateFiles.push(file)
                        } else if (e.code === 'special-characters') {
                            specialCharsItems.push(file)
                        }
                    })
                })

            if (invalidFiles.length > 0) {
                setInvalidFile(invalidFiles)
            }

            if (duplicateFiles.length > 0) {
                setDuplicateFile(duplicateFiles)
            }

            if (emptyFiles.length > 0) {
                setEmptyFile(emptyFiles)
            }

            if (tooBigFiles.length > 0) {
                setBigFile(tooBigFiles)
            }

            if (tooBigFileName.length > 0) {
                setBigFileName(tooBigFileName)
            }

            if (specialCharsItems.length > 0) {
                setSpecialCharactersFiles(specialCharsItems)
            }
            if (
                invalidFiles.length === 0 &&
                duplicateFiles.length === 0 &&
                emptyFiles.length === 0 &&
                tooBigFiles.length === 0 &&
                tooBigFileName.length === 0 &&
                !tooBigBatch
            ) {
                onUpload()
            }
        },
        [chatSettings?.processingStrategyTranslation.docs.length, onUpload, setIsUploading],
    )

    const getUniqueElement = (files: ITranslatedDocs[], newPath: string) => {
        const newList = files
        const basename = getBaseName(newPath)
        const extension = newPath.includes('.') ? newPath.substring(newPath.lastIndexOf('.')) : ''
        let newElementPath = basename + extension

        if (newList.some((obj) => getBaseName(obj.name) === basename)) {
            const suffix = getNextAvailableSuffix(newList, basename)
            newElementPath = `${basename}_${suffix}${extension}`
        }

        return newElementPath
    }

    const getNextAvailableSuffix = (list: ITranslatedDocs[], basename: string) => {
        let suffix = 0
        list.forEach((obj) => {
            const base = getBaseName(obj.name)
            if (base.startsWith(basename + '_')) {
                const currentSuffix = parseInt(base.substring(basename.length + 1), 10)
                if (!isNaN(currentSuffix) && currentSuffix > suffix) {
                    suffix = currentSuffix
                }
            }
        })
        return suffix + 1
    }

    const onSetKeepBoth = (files: File[]) => {
        const fileItems: Array<File> = duplicateFile
        const keepFiles = files.map((file) => {
            const fileList = chatSettings?.processingStrategyTranslation.docs || []
            const uniqueName = getUniqueElement(fileList, file.name)
            const fileToSave = new File([file], uniqueName, {
                type: file.type,
                lastModified: file.lastModified,
            })

            return fileToSave
        })

        // set files where we have confirmation from user
        setValidFiles([...validFiles, ...keepFiles])

        const difference = fileItems.filter((item1) => !files.find((item2) => item2.name === item1.name))

        // set remaining files for there are no resolution yet
        setDuplicateFile(difference)
    }

    const onSetReplacedFiles = (files: File[]) => {
        const fileItems: Array<File> = duplicateFile

        const difference = fileItems.filter((item1) => !files.find((item2) => item2.name === item1.name))

        // set files where we have confirmation from user
        setValidFiles([...validFiles, ...files])
        // set remaining files for there are no resolution yet
        setDuplicateFile(difference)
    }

    const getAcceptedFiles = (): Accept | undefined => {
        if (fileExtensions) {
            const acceptedObject: Accept = {}
            fileExtensions.forEach((item) => {
                acceptedObject[item.mimeType] = item.fileExtensions
            })
            return acceptedObject
        }
    }

    const { getRootProps, getInputProps } = useDropzone({
        accept: getAcceptedFiles(),
        onDrop,
        validator: fileValidator,
        useFsAccessApi: false,
    })

    const onInvalidFileDrawerClose = () => {
        setInvalidFile([])
        if (!emptyFile.length && !duplicateFile.length && isUploading) {
            setIsUploading(false)
        }
    }

    const onEmptyFileDrawerClose = () => {
        setEmptyFile([])
        if (!invalidFile.length && !duplicateFile.length && isUploading) {
            setIsUploading(false)
        }
    }

    const onBigFileDrawerClose = () => {
        setBigFile([])
        if (!emptyFile.length && !invalidFile.length && !duplicateFile.length && isUploading) {
            setIsUploading(false)
        }
    }

    const onBigFileNameDrawerClose = () => {
        setBigFileName([])
        if (!emptyFile.length && !invalidFile.length && !duplicateFile.length && !bigFile.length && isUploading) {
            setIsUploading(false)
        }
    }

    const onTooBigBatchOfFilesDrawerClose = () => {
        setIsTooBig(false)
        if (!emptyFile.length && !invalidFile.length && !duplicateFile.length && !bigFile.length && isUploading) {
            setIsUploading(false)
        }
    }

    const onTooManyFilesToUploadDrawerClose = () => {
        setIsTooManyFilesError(false)
        setIsUploading(false)
    }

    const onTotalNumberOfFilesError = () => {
        setIsOverTotalNumbersOfFiles(false)
        setIsUploading(false)
    }

    const onDuplicateFileDrawerClose = () => {
        setDuplicateFile([])
        if (!invalidFile.length && !emptyFile.length && isUploading) {
            setIsUploading(false)
        }
    }

    const onInvalidCharactersFileDrawerClose = () => {
        setSpecialCharactersFiles([])
        if (!emptyFile.length && !invalidFile.length && !duplicateFile.length && !bigFile.length && isUploading) {
            setIsUploading(false)
        }
    }

    useEffect(() => {
        if (invalidFile.length === 0 && validFiles.length > 0) {
            onUpload()
        }
    }, [invalidFile, validFiles, onUpload, isUploading])

    useEffect(() => {
        async function getChatHealth() {
            const response = await checkHealth()
            if (response) {
                setIsVpn(true)
            } else {
                setIsVpn(false)
            }
        }

        void getChatHealth()
    }, [])

    return (
        <>
            {isVpn && quotas && quotas.individual.creditsRemaining && (
                <DropZone
                    getRootProps={getRootProps}
                    getInputProps={getInputProps}
                    isUploading={isUploading}
                    isFullHeight={chatSettings?.processingStrategyTranslation.docs.length === 0}
                />
            )}
            {isVpn === false && <ProhibitedUpload isVpn={true} />}
            {isVpn !== false && quotas && !quotas.individual.creditsRemaining && <ProhibitedUpload isVpn={false} />}
            <InvalidFileDrawer files={invalidFile} isOpen={invalidFile.length > 0} onClose={onInvalidFileDrawerClose} />
            <InvalidCharactersFileNameDrawer
                isOpen={specialCharsFiles.length > 0 && invalidFile.length === 0}
                files={specialCharsFiles}
                onClose={onInvalidCharactersFileDrawerClose}
            />
            <EmptyFileDrawer
                files={emptyFile}
                isOpen={emptyFile.length > 0 && invalidFile.length === 0 && specialCharsFiles.length === 0}
                onClose={onEmptyFileDrawerClose}
            />
            <BigFileDrawer
                files={bigFile}
                isOpen={bigFile.length > 0 && invalidFile.length === 0 && emptyFile.length === 0 && specialCharsFiles.length === 0}
                onClose={onBigFileDrawerClose}
            />
            <BigFileNameDrawer
                files={bigFileName}
                isOpen={
                    bigFileName.length > 0 &&
                    invalidFile.length === 0 &&
                    emptyFile.length === 0 &&
                    bigFile.length === 0 &&
                    specialCharsFiles.length > 0
                }
                onClose={onBigFileNameDrawerClose}
            />
            <TooBigBatchFilesDrawer
                isOpen={
                    isTooBig && invalidFile.length === 0 && emptyFile.length === 0 && bigFile.length === 0 && specialCharsFiles.length === 0
                }
                onClose={onTooBigBatchOfFilesDrawerClose}
            />

            <TooManyFilesToUpload isOpen={isTooManyFilesError} onClose={onTooManyFilesToUploadDrawerClose} />
            <MoreThaOneHundredToUpload
                isOpen={isOverTotalNumbersOfFiles}
                noOfFiles={chatSettings?.processingStrategyTranslation.docs.length || 0}
                onClose={onTotalNumberOfFilesError}
            />
            <ResolutionDrawer
                files={duplicateFile}
                isOpen={
                    invalidFile.length === 0 &&
                    emptyFile.length === 0 &&
                    duplicateFile.length > 0 &&
                    !isTooBig &&
                    specialCharsFiles.length === 0
                }
                onClose={onDuplicateFileDrawerClose}
                onSetReplacedFiles={onSetReplacedFiles}
                onSetKeepBoth={onSetKeepBoth}
            />
        </>
    )
}

export default Container
